import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { useState, useEffect } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { UPDATE_CAC_REG_STATUS_WITH_FILES, RESEND_CAC_REGISTRATION_EMAIL } from '../../../graphql/mutations/cacClient';
import { GET_SIGNED_FILE_URL } from '../../../graphql/queries/cacClient';
import { UPLOAD_IMAGE } from '../../../graphql/mutations/companyRegistration';

// Styled components for file upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const StyledFilePreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
}));

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'queried', label: 'Queried' },
  { value: 'queried-by-admin', label: 'Queried by Admin' },
  { value: 'conditional_approval', label: 'Conditional Approval' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' }
];

export default function StatusUpdateForm({ application, onSuccess }) {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState(application?.status || 'pending');
  const [certificateUrl, setCertificateUrl] = useState(application?.certificate || null);
  const [statusReportUrl, setStatusReportUrl] = useState(application?.statusReport || null);
  const [certificateUploading, setCertificateUploading] = useState(false);
  const [statusReportUploading, setStatusReportUploading] = useState(false);

  const [viewingFile, setViewingFile] = useState(null);

  const [uploadImage] = useMutation(UPLOAD_IMAGE);
  const [getSignedFileUrl] = useLazyQuery(GET_SIGNED_FILE_URL, {
    fetchPolicy: 'network-only',
  });

  const handleViewFile = async (fileUrl, fileKey) => {
    if (!fileUrl) return;
    setViewingFile(fileKey);
    try {
      const { data } = await getSignedFileUrl({ variables: { fileUrl } });
      const signedUrl = data?.getSignedFileUrl;
      if (signedUrl) {
        window.open(signedUrl, '_blank');
      } else {
        enqueueSnackbar('Could not generate a link for this file', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to open file', { variant: 'error' });
    } finally {
      setViewingFile(null);
    }
  };

  const [updateStatus, { loading }] = useMutation(UPDATE_CAC_REG_STATUS_WITH_FILES, {
    onCompleted: (data) => {
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
      if (onSuccess) {
        onSuccess(data.updateCacRegStatusWithFiles);
      }
    },
    onError: (error) => {
      enqueueSnackbar(error.message || 'Failed to update status', { variant: 'error' });
    }
  });

  const [resendEmail, { loading: resending }] = useMutation(RESEND_CAC_REGISTRATION_EMAIL, {
    onCompleted: () => {
      enqueueSnackbar('Approval email resent to applicant', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error.message || 'Failed to resend email', { variant: 'error' });
    }
  });

  const handleResendEmail = () => {
    if (!application?._id) return;
    resendEmail({ variables: { id: application._id } });
  };

  // Reset form when application changes
  useEffect(() => {
    setStatus(application?.status || 'pending');
    setCertificateUrl(application?.certificate || null);
    setStatusReportUrl(application?.statusReport || null);
  }, [application]);

  const handleStatusChange = (event) => {
    setStatus(event.target.value);
  };

  const handleCertificateUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setCertificateUploading(true);
      try {
        const { data } = await uploadImage({
          variables: { image: file }
        });
        setCertificateUrl(data.uploadImage);
        enqueueSnackbar('Certificate uploaded successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to upload certificate', { variant: 'error' });
      } finally {
        setCertificateUploading(false);
      }
    }
  };

  const handleStatusReportUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setStatusReportUploading(true);
      try {
        const { data } = await uploadImage({
          variables: { image: file }
        });
        setStatusReportUrl(data.uploadImage);
        enqueueSnackbar('Status report uploaded successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to upload status report', { variant: 'error' });
      } finally {
        setStatusReportUploading(false);
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Show file uploads for approved and conditional approval statuses
    const needsFiles = ['approved', 'conditional_approval'].includes(status);
    
    try {
      await updateStatus({
        variables: {
          input: {
            id: application._id,
            status,
            certificate: needsFiles ? certificateUrl : null,
            statusReport: needsFiles ? statusReportUrl : null
          }
        }
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Update Application Status
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="status-select-label">Status</InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={status}
                label="Status"
                onChange={handleStatusChange}
                disabled={loading}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Select the new status for this application
              </FormHelperText>
            </FormControl>
          </Grid>

          {['approved', 'conditional_approval'].includes(status) && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Certificate
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  disabled={loading || certificateUploading}
                  fullWidth
                >
                  {certificateUploading ? 'Uploading...' : certificateUrl ? 'Change Certificate' : 'Upload Certificate'}
                  <VisuallyHiddenInput 
                    type="file" 
                    onChange={handleCertificateUpload} 
                    accept="image/*,.pdf"
                    disabled={certificateUploading}
                  />
                </Button>
                {certificateUrl && (
                  <StyledFilePreview>
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      Certificate uploaded ✓
                    </Typography>
                    <Button 
                      size="small"
                      disabled={viewingFile === 'certificate'}
                      onClick={() => handleViewFile(certificateUrl, 'certificate')}
                    >
                      {viewingFile === 'certificate' ? 'Opening...' : 'View'}
                    </Button>
                  </StyledFilePreview>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Status Report
                </Typography>
                <Button
                  component="label"
                  variant="outlined"
                  disabled={loading || statusReportUploading}
                  fullWidth
                >
                  {statusReportUploading ? 'Uploading...' : statusReportUrl ? 'Change Status Report' : 'Upload Status Report'}
                  <VisuallyHiddenInput 
                    type="file" 
                    onChange={handleStatusReportUpload} 
                    accept="image/*,.pdf"
                    disabled={statusReportUploading}
                  />
                </Button>
                {statusReportUrl && (
                  <StyledFilePreview>
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      Status report uploaded ✓
                    </Typography>
                    <Button 
                      size="small"
                      disabled={viewingFile === 'statusReport'}
                      onClick={() => handleViewFile(statusReportUrl, 'statusReport')}
                    >
                      {viewingFile === 'statusReport' ? 'Opening...' : 'View'}
                    </Button>
                  </StyledFilePreview>
                )}
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={loading}
              fullWidth
            >
              Update Status
            </LoadingButton>
          </Grid>

          {['approved', 'conditional_approval'].includes(status) &&
            certificateUrl &&
            statusReportUrl && (
              <Grid item xs={12}>
                <LoadingButton
                  variant="outlined"
                  color="secondary"
                  loading={resending}
                  onClick={handleResendEmail}
                  fullWidth
                >
                  Resend Approval Email to Applicant
                </LoadingButton>
                <FormHelperText>
                  Resends the approval email with the certificate and status report attached.
                </FormHelperText>
              </Grid>
            )}
        </Grid>
      </form>
    </Card>
  );
}
